import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getDeliverable,
  listDeliverables,
} from '../../services/deliverablesApi';
import FilterMenu, { DEFAULT_FILTERS } from './FilterMenu';
import GanttDetailPanel from './GanttDetailPanel';
import { Chip, DrawerDetail, ErrorBanner, Spinner, T } from './shared';

/* ============================================================================
 * Insights: Service-line Gantt of project deliverables. Ported near-verbatim
 * from the reference `ServiceLineGantt` component, backed by the real
 * deliverables API instead of a hardcoded SEED array. Data boundary: the
 * `jira` block is display-only (would be live Jira data on a real project;
 * here it's the mocked jira_* fields the backend stores when
 * source_system !== "JIRA"). Detail panel opens in the existing `DrawerDetail`
 * slide-in instead of the reference's docked side-by-side layout.
 * ========================================================================== */

export const ZOOMS = {
  // 40 wasn't enough room for an "11 Sept"-width label (~48-50px) next to
  // its neighbor — every date ran into the one beside it. 60 gives each
  // label breathing room without changing WEEK/MONTH, which only label
  // every 7th/30th column so they were never actually crowded.
  DAY: { key: 'DAY', label: 'Day', pxPerDay: 60 },
  WEEK: { key: 'WEEK', label: 'Week', pxPerDay: 14 },
  MONTH: { key: 'MONTH', label: 'Month', pxPerDay: 5 },
};

export const ROW_H = 44;
export const BAR_H = 18;
const DAY_MS = 86400000;

export const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

export const d = (x) => {
  if (!x) return null;
  if (x instanceof Date) return x;
  return new Date(String(x).includes('T') ? x : `${x}T00:00:00Z`);
};
export const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);
const addDays = (base, n) => new Date(base.getTime() + n * DAY_MS);
const addDaysIso = (iso, n) => addDays(d(iso) || TODAY, n).toISOString();

export const fmtDate = (x) => {
  if (!x) return '—';
  const dt = typeof x === 'string' ? d(x) : x;
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
export const humanise = (s) => {
  if (!s) return '—';
  return String(s).replace(/_/g, ' ').toLowerCase();
};

/** Works for a deliverable's jira block or a raw subtask -- same date fields. */
export const barWindow = (j) => {
  const due = d(j.due_date) || TODAY;
  if (j.status === 'Done')
    return {
      start: d(j.started_at) || due,
      end: d(j.completed_at) || due,
      hollow: false,
    };
  if (j.started_at) return { start: d(j.started_at), end: due, hollow: false };
  return { start: TODAY < due ? TODAY : due, end: due, hollow: true };
};

export const prereqCounts = (t) => {
  const p = t.prerequisites ?? [];
  const unresolved = p.filter(
    (x) => x.verification_status !== 'PASS' && x.status !== 'MET',
  );
  return {
    total: p.length,
    pass: p.length - unresolved.length,
    open: unresolved.length,
    unresolved,
  };
};

/** On Track / At Risk / Blocked / Overdue -- computed at render, never stored. */
export const riskState = (t) => {
  const j = t.jira;
  if (j.status === 'Done') return 'DONE';
  const due = d(j.due_date) || TODAY;
  if (due < TODAY) return 'OVERDUE';
  if (
    t.open_concerns?.some((c) => c.subtype === 'BLOCKER' && c.status === 'OPEN')
  )
    return 'BLOCKED';
  if (t.readiness_status === 'NOT_READY') return 'BLOCKED';

  const daysLeft = daysBetween(TODAY, due);
  const prereqOpen = prereqCounts(t).open > 0;
  const assumptionSoon = t.open_assumptions?.some(
    (a) => daysBetween(TODAY, d(a.verify_by)) <= 5,
  );
  const depOpen = t.open_concerns?.some(
    (c) => c.subtype === 'DEPENDENCY' && c.status === 'OPEN',
  );

  if (daysLeft <= 5 && prereqOpen) return 'AT_RISK';
  if (assumptionSoon || depOpen) return 'AT_RISK';
  if (t.readiness_status === 'AMBIGUOUS') return 'AT_RISK';
  return 'ON_TRACK';
};

/** Subtasks carry no Oqlous intelligence -- schedule state only. */
export const subtaskState = (s) => {
  if (s.status === 'Done') return 'DONE';
  if ((d(s.due_date) || TODAY) < TODAY) return 'OVERDUE';
  return s.started_at ? 'ON_TRACK' : 'NOT_STARTED';
};

export const riskTone = (s) => {
  switch (s) {
    case 'OVERDUE':
      return { fg: T.danger, bg: T.dangerBg, bar: T.danger, label: 'Overdue' };
    case 'BLOCKED':
      return { fg: T.danger, bg: T.dangerBg, bar: '#b45c5c', label: 'Blocked' };
    case 'AT_RISK':
      return { fg: T.warn, bg: T.warnBg, bar: T.warn, label: 'At risk' };
    case 'DONE':
      return { fg: T.ok, bg: T.okBg, bar: '#3f7a55', label: 'Done' };
    case 'NOT_STARTED':
      return {
        fg: T.muted,
        bg: 'rgba(135,135,127,0.12)',
        bar: T.muted,
        label: 'Not started',
      };
    default:
      return { fg: T.info, bg: T.infoBg, bar: T.info, label: 'On track' };
  }
};
export const readinessTone = (s) => {
  switch (s) {
    case 'READY':
      return { fg: T.ok, label: 'Ready' };
    case 'PARTIALLY_READY':
      return { fg: T.warn, label: 'Partially ready' };
    case 'NOT_READY':
      return { fg: T.danger, label: 'Not ready' };
    default:
      return { fg: T.muted, label: 'Ambiguous' };
  }
};
export const hasWarning = (t) => {
  if (t.jira.status === 'Done') return false;
  return (
    prereqCounts(t).open > 0 ||
    !!t.open_assumptions?.some((a) => daysBetween(TODAY, d(a.verify_by)) <= 7)
  );
};
export const needsAttention = (t) =>
  ['OVERDUE', 'BLOCKED', 'AT_RISK'].includes(riskState(t));

export const WarnIcon = ({ color = T.warn, size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8 2L15 14H1L8 2Z"
      stroke={color}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M8 6.5V9.5"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11.6" r="0.8" fill={color} />
  </svg>
);
export const Caret = ({ open }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 12 12"
    fill="none"
    style={{
      transform: open ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
    }}
  >
    <path
      d="M4 2.5L8 6L4 9.5"
      stroke={T.muted}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Raw `/api/deliverables` row -> the `ticket` shape the ported UI expects. */
const toTicket = (dv, scopeOverride) => {
  const dueDate = dv.jira_due_date || addDaysIso(dv.jira_started_at, 7);
  return {
    deliverable_id: dv.deliverable_id,
    // No raw-id fallback here -- an operational deliverable with no Jira
    // ticket has no ticket ref to show, not a fake one made of its Mongo id.
    external_ticket_id: dv.external_ticket_id || null,
    service_line_id: dv.service_line_id,
    activity_id: dv.activity_id,
    activity_name: dv.activity_id || dv.service_line_id || 'Deliverable',
    client_type_label: dv.client_type_label || null,
    client_requirement: dv.client_requirement || null,
    internal_status: dv.internal_status || null,
    internal_status_since: dv.internal_status_since || dv.created_at || null,
    readiness_status: dv.readiness_status,
    readiness_basis: dv.readiness_basis,
    scope: scopeOverride || {
      in_scope: [],
      out_of_scope: [],
      corrected: false,
    },
    sop_steps: dv.sop_steps || [],
    prerequisites: dv.prerequisites || [],
    acceptance_criteria: dv.acceptance_criteria || [],
    open_assumptions: dv.open_assumptions || [],
    open_concerns: (dv.concerns || []).map((c) => ({
      concern_id: c.entity_id,
      subtype: c.subtype,
      description: c.description,
      status: 'OPEN',
    })),
    approvals: dv.approvals || [],
    learnings: dv.learnings || [],
    blocked_by_deliverables: dv.blocked_by_deliverables || [],
    blocks_deliverables: dv.blocks_deliverables || [],
    jira: {
      title: dv.jira_title || dv.ops_type || 'Untitled deliverable',
      owner: dv.jira_owner || 'Unassigned',
      status: dv.jira_status || 'To Do',
      priority: dv.jira_priority || 'MEDIUM',
      started_at: dv.jira_started_at || null,
      due_date: dueDate,
      completed_at: dv.jira_completed_at || null,
      subtasks: (dv.jira_subtasks || []).map((s) => ({
        ...s,
        due_date: s.due_date || addDaysIso(s.started_at, 3),
      })),
    },
  };
};

// Columns are the deliverable's own workflow stage, not its Jira status --
// Jira has no concept of a clarification gate or a readiness rollup, so a
// board built on Jira status can't show where work is actually stuck.
const KANBAN_COLUMNS = [
  { key: 'INTAKE', label: 'Intake' },
  { key: 'CLARIFICATION_REQUIRED', label: 'Clarification needed' },
  { key: 'READINESS_CHECK', label: 'Readiness check' },
  { key: 'READY', label: 'Ready' },
  { key: 'IN_EXECUTION', label: 'In execution' },
  { key: 'ON_HOLD', label: 'On hold' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'UNMAPPED', label: 'Unmapped' },
];

const KanbanCardHeadline = ({ ticket }) => {
  const rd = readinessTone(ticket.readiness_status);
  if (ticket.internal_status === 'READINESS_CHECK') {
    const pc = prereqCounts(ticket);
    const pct = pc.total ? (pc.pass / pc.total) * 100 : 0;
    return (
      <div className="mt-2 flex items-center gap-2">
        <div
          className="h-[5px] flex-1 overflow-hidden rounded-full"
          style={{ background: T.bg1 }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: pct === 100 ? T.ok : T.warn,
            }}
          />
        </div>
        <span className="text-[10px]" style={{ color: T.muted }}>
          {pc.pass}/{pc.total}
        </span>
      </div>
    );
  }
  if (ticket.internal_status === 'ON_HOLD' && ticket.readiness_basis) {
    return (
      <p className="mt-2 text-[11px] leading-snug" style={{ color: T.textSec }}>
        {ticket.readiness_basis}
      </p>
    );
  }
  if (!ticket.readiness_status) return null;
  return (
    <div className="mt-2">
      <Chip fg={rd.fg} bg={rd.bg}>
        {rd.label}
      </Chip>
    </div>
  );
};

/** Same tickets as the Gantt, grouped into columns by workflow stage instead of time. */
const KanbanBoard = ({ tickets, selectedId, onSelect }) => {
  const columns = KANBAN_COLUMNS.map((col) => ({
    ...col,
    items: tickets.filter((t) => (t.internal_status || 'UNMAPPED') === col.key),
  })).filter((col) => col.key !== 'UNMAPPED' || col.items.length > 0);

  return (
    <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
      {columns.map((col) => (
        <div
          key={col.key}
          className="flex w-72 shrink-0 flex-col rounded-2xl border border-zinc-800/60 bg-zinc-950/60 shadow-lg backdrop-blur-sm"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-zinc-800/60 px-4 py-3">
            <span className="text-xs font-semibold text-zinc-200">
              {col.label}
            </span>
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] font-bold text-zinc-400">
              {col.items.length}
            </span>
          </div>
          {/* Only this card list scrolls (per column) — the column header
              above stays put, same as the Gantt's row list below.
              overflow-x-hidden is explicit for the same reason as the
              Gantt's timeline div: overflow-y-auto alone leaves overflow-x
              to compute as auto too, per the CSS overflow spec. */}
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden p-2.5">
            {col.items.length === 0 && (
              <div className="py-6 text-center text-xs text-zinc-500">
                Nothing here
              </div>
            )}
            {col.items.map((t) => {
              const isSel = t.deliverable_id === selectedId;
              return (
                <button
                  key={t.deliverable_id}
                  type="button"
                  onClick={() => onSelect(t.deliverable_id)}
                  className={`rounded-xl p-3 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40 ${
                    isSel
                      ? 'border border-indigo-500/60 bg-indigo-950/20 shadow-md shadow-indigo-950/30 ring-1 ring-indigo-500/50'
                      : 'border border-zinc-800/70 bg-zinc-900/70 shadow-sm hover:border-zinc-700 hover:bg-zinc-800/70'
                  }`}
                >
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-medium text-zinc-400">
                      {t.external_ticket_id || 'Internal'}
                    </span>
                    {t.client_type_label ? (
                      <Chip fg={T.info} bg={T.infoBg}>
                        {humanise(t.client_type_label)}
                      </Chip>
                    ) : (
                      <Chip fg={T.muted} bordered>
                        unmapped
                      </Chip>
                    )}
                  </div>
                  <div className="line-clamp-2 text-[13px] font-medium leading-snug text-zinc-100">
                    {t.jira.title}
                  </div>
                  <p className="mt-1 text-[11px] text-zinc-400">
                    {t.activity_name} ·{' '}
                    <span className="text-zinc-300">{t.jira.owner}</span>
                  </p>

                  <KanbanCardHeadline ticket={t} />

                  <div className="mt-3 flex items-center gap-2.5 border-t border-zinc-800/70 pt-2.5 text-[11px]">
                    {t.open_concerns.length > 0 && (
                      <span className="flex items-center gap-1">
                        {t.open_concerns.slice(0, 3).map((c) => (
                          <span
                            key={c.concern_id}
                            className="inline-block h-[6px] w-[6px] rounded-full"
                            style={{
                              background:
                                c.subtype === 'BLOCKER'
                                  ? T.danger
                                  : c.subtype === 'RISK'
                                    ? T.warn
                                    : T.info,
                            }}
                          />
                        ))}
                        <span className="text-[10px] font-medium text-zinc-400">
                          {t.open_concerns.length}
                        </span>
                      </span>
                    )}
                    {t.blocked_by_deliverables.length > 0 && (
                      <span className="text-[10px] font-medium text-amber-400">
                        ⇠{t.blocked_by_deliverables.length}
                      </span>
                    )}
                    {t.jira.subtasks.length > 0 && (
                      <span className="text-[10px] text-zinc-400">
                        ⊞{t.jira.subtasks.length}
                      </span>
                    )}
                    <span className="ml-auto text-[10px] font-medium text-zinc-400">
                      due {fmtDate(t.jira.due_date)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// `view`/`zoom` are still owned by the parent (ProjectRegisterPage) — this
// component just renders the controls for them, alongside FilterMenu, on
// one row: Day/Week/Month + Gantt/Kanban on the left, Filter on the right.
const InsightsGantt = ({
  accessToken,
  view = 'GANTT',
  zoom = 'WEEK',
  onViewChange = () => {},
  onZoomChange = () => {},
  onStatsChange = () => {},
}) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [detailScope, setDetailScope] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  // A Gantt row is two separate DOM elements (left label column, right
  // timeline column) — plain CSS :hover on each independently means
  // hovering one half doesn't highlight the other half of the same row.
  // Tracked here instead so both sides light up together.
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const scrollRef = useRef(null);
  // Mirrors the timeline body's horizontal scroll onto the date-ticks
  // header above it (see the body's onScroll below) — the header never
  // scrolls on its own; the only real scrollbar there is the body's.
  const headerScrollRef = useRef(null);
  const pxPerDay = ZOOMS[zoom].pxPerDay;

  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listDeliverables(accessToken)
      .then((response) => {
        if (!cancelled)
          setTickets((response?.deliverables || []).map((dv) => toTicket(dv)));
      })
      .catch((loadError) => {
        if (!cancelled) setError(String(loadError?.message || loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!selectedId || !accessToken) {
      setDetailScope(null);
      return;
    }
    let cancelled = false;
    getDeliverable(accessToken, selectedId)
      .then((response) => {
        if (!cancelled) setDetailScope(response?.scope || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedId, accessToken]);

  const serviceLines = useMemo(
    () =>
      [
        ...new Set(tickets.map((t) => t.service_line_id).filter(Boolean)),
      ].sort(),
    [tickets],
  );
  const activities = useMemo(
    () =>
      [...new Set(tickets.map((t) => t.activity_id).filter(Boolean))].sort(),
    [tickets],
  );
  const owners = useMemo(
    () => [...new Set(tickets.map((t) => t.jira.owner))].sort(),
    [tickets],
  );

  const matches = useCallback(
    (t) => {
      if (
        filters.serviceLine !== 'ALL' &&
        t.service_line_id !== filters.serviceLine
      )
        return false;
      if (filters.activity !== 'ALL' && t.activity_id !== filters.activity)
        return false;
      if (filters.status !== 'ALL' && t.jira.status !== filters.status)
        return false;
      if (filters.owner !== 'ALL' && t.jira.owner !== filters.owner)
        return false;
      if (
        filters.readiness !== 'ALL' &&
        t.readiness_status !== filters.readiness
      )
        return false;
      if (filters.risk !== 'ALL' && riskState(t) !== filters.risk) return false;
      if (filters.priority !== 'ALL' && t.jira.priority !== filters.priority)
        return false;
      if (
        filters.dueWithin !== 'ALL' &&
        daysBetween(TODAY, d(t.jira.due_date)) > Number(filters.dueWithin)
      )
        return false;
      if (filters.attentionOnly && !needsAttention(t)) return false;
      return true;
    },
    [filters],
  );

  const rows = useMemo(() => {
    const out = [];
    tickets.filter(matches).forEach((t) => {
      out.push({
        kind: 'TICKET',
        id: t.deliverable_id,
        ticket: t,
        hasChildren: (t.jira.subtasks ?? []).length > 0,
      });
      if (expanded[t.deliverable_id]) {
        (t.jira.subtasks ?? []).forEach((s) =>
          out.push({
            kind: 'SUBTASK',
            id: `${t.deliverable_id}:${s.key}`,
            subtask: s,
            parent: t,
            hasChildren: false,
          }),
        );
      }
    });
    return out;
  }, [tickets, matches, expanded]);

  const { rangeStart, totalDays } = useMemo(() => {
    const pts = [TODAY];
    tickets.forEach((t) => {
      const w = barWindow(t.jira);
      pts.push(w.start, w.end);
      (t.jira.subtasks ?? []).forEach((s) => {
        const sw = barWindow(s);
        pts.push(sw.start, sw.end);
      });
    });
    const min = new Date(Math.min(...pts.map((x) => x.getTime())));
    const max = new Date(Math.max(...pts.map((x) => x.getTime())));
    const start = addDays(min, -3);
    return { rangeStart: start, totalDays: daysBetween(start, max) + 6 };
  }, [tickets]);

  const xOf = useCallback(
    (date) => daysBetween(rangeStart, date) * pxPerDay,
    [rangeStart, pxPerDay],
  );
  const gridW = totalDays * pxPerDay;

  const ticks = useMemo(() => {
    const out = [];
    const step = zoom === 'DAY' ? 1 : zoom === 'WEEK' ? 7 : 30;
    for (let i = 0; i < totalDays; i += step) {
      const date = addDays(rangeStart, i);
      out.push({
        x: i * pxPerDay,
        label:
          zoom === 'MONTH'
            ? date.toLocaleDateString('en-GB', { month: 'short' })
            : date.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
              }),
      });
    }
    return out;
  }, [rangeStart, totalDays, pxPerDay, zoom]);

  const connectors = useMemo(() => {
    const byDeliverable = new Map();
    const bySubtaskKey = new Map();
    rows.forEach((r, i) => {
      if (r.kind === 'TICKET') {
        byDeliverable.set(r.ticket.deliverable_id, {
          i,
          win: barWindow(r.ticket.jira),
          late: ['OVERDUE', 'BLOCKED'].includes(riskState(r.ticket)),
        });
      } else {
        bySubtaskKey.set(r.subtask.key, {
          i,
          win: barWindow(r.subtask),
          late: subtaskState(r.subtask) === 'OVERDUE',
        });
      }
    });

    const out = [];
    rows.forEach((r, i) => {
      const deps =
        r.kind === 'TICKET'
          ? (r.ticket.blocked_by_deliverables ?? [])
          : (r.subtask.depends_on ?? []);
      const lookup = r.kind === 'TICKET' ? byDeliverable : bySubtaskKey;
      const myWin =
        r.kind === 'TICKET' ? barWindow(r.ticket.jira) : barWindow(r.subtask);
      deps.forEach((upKey) => {
        const up = lookup.get(upKey);
        if (!up) return;
        // Hollow bars are unscheduled placeholders (today->due), not real
        // dates -- wiring a connector to/from one draws backward through
        // the rows above it. Skip until both ends have a real schedule.
        if (up.win.hollow || myWin.hollow) return;
        out.push({
          key: `${upKey}->${r.id}`,
          x1: xOf(up.win.end),
          y1: up.i * ROW_H + ROW_H / 2,
          x2: xOf(myWin.start),
          y2: i * ROW_H + ROW_H / 2,
          late: up.late,
        });
      });
    });
    return out;
  }, [rows, xOf]);

  const attentionCount = useMemo(
    () => tickets.filter(needsAttention).length,
    [tickets],
  );
  const learningCount = useMemo(
    () => tickets.reduce((n, t) => n + (t.learnings?.length ?? 0), 0),
    [tickets],
  );

  // Reported up so the page can render this summary next to the sidebar's
  // collapse/expand button instead of inside this component's own content.
  useEffect(() => {
    onStatsChange({ total: tickets.length, attentionCount, learningCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets.length, attentionCount, learningCount]);

  const selected = tickets.find((t) => t.deliverable_id === selectedId) ?? null;
  const selectedWithScope =
    selected && detailScope ? { ...selected, scope: detailScope } : selected;

  if (loading) return <Spinner />;

  return (
    // h-full + min-h-0: fills whatever's left of the page frame exactly,
    // instead of growing past it. The toolbar/filter row and stats line
    // below stay put (shrink-0); only the Gantt/Kanban body scrolls, in
    // its own overflow-y-auto — the page itself no longer scrolls the
    // filters away along with the list.
    <div className="flex h-full min-h-0 flex-col gap-2">
      <ErrorBanner>{error}</ErrorBanner>

      {/* Gantt/Kanban + Day/Week/Month on the left, Filter on the right —
          one toolbar row instead of the two this used to be split across. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-xl border border-zinc-800/70 bg-zinc-900/70 p-1 shadow-inner">
            {[
              { key: 'GANTT', label: 'Gantt' },
              { key: 'KANBAN', label: 'Kanban' },
            ].map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => onViewChange(v.key)}
                className={`rounded-lg px-3 py-1 text-xs font-medium transition-all ${
                  view === v.key
                    ? 'bg-primary-gradient font-semibold text-white shadow-md shadow-indigo-950/40'
                    : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {view === 'GANTT' && (
            <div className="flex items-center gap-1.5 rounded-xl border border-zinc-800/70 bg-zinc-900/70 p-1 shadow-inner">
              {Object.values(ZOOMS).map((z) => (
                <button
                  key={z.key}
                  type="button"
                  onClick={() => onZoomChange(z.key)}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-all ${
                    zoom === z.key
                      ? 'bg-primary-gradient font-semibold text-white shadow-md shadow-indigo-950/40'
                      : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
                  }`}
                >
                  {z.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <FilterMenu
          filters={filters}
          setFilters={setFilters}
          serviceLines={serviceLines}
          activities={activities}
          owners={owners}
          attentionCount={attentionCount}
        />
      </div>

      {/* Desktop shows this next to the sidebar's collapse/expand button
          instead (see ProjectRegisterPage, fed by onStatsChange above);
          mobile has no room up there, so it stays in-flow here. */}
      <p className="shrink-0 pl-2.5 text-xs font-medium text-zinc-400 sm:hidden">
        <span className="font-semibold text-zinc-200">{tickets.length}</span>{' '}
        deliverables ·{' '}
        <span className="font-semibold text-amber-400">{attentionCount}</span>{' '}
        need attention
        {/* Catalogue learnings hidden for now — not ready to surface yet. */}
      </p>

      {view === 'KANBAN' ? (
        <KanbanBoard
          tickets={tickets.filter(matches)}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/70 shadow-xl backdrop-blur-md">
          {/* Header row: "Deliverable" heading + date ticks. Stays fixed
              while the body below scrolls vertically; its horizontal
              position is driven off the body's own horizontal scroll
              (see the body's onScroll), so it still tracks Day/Week/Month
              zoom scrolling without a scrollbar of its own. */}
          <div className="flex shrink-0">
            <div className="flex h-11 w-[310px] shrink-0 items-center border-b border-r border-zinc-800/60 bg-zinc-950/60 px-4 text-[11px] font-semibold uppercase tracking-wider text-zinc-300">
              Deliverable
            </div>
            <div
              ref={headerScrollRef}
              className="min-w-0 flex-1 overflow-hidden"
            >
              <div style={{ width: gridW, minWidth: '100%' }}>
                <div className="relative h-11 border-b border-zinc-800/60 bg-zinc-950/60">
                  {ticks.map((t, i) => (
                    <div
                      key={i}
                      className="absolute bottom-2.5 whitespace-nowrap text-[11px] font-medium text-zinc-400"
                      style={{ left: t.x + 6 }}
                    >
                      {t.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Body: deliverable rows (left) + timeline bars (right) share
              this one vertical scroll, so the two columns move together
              with a single scrollbar — the toolbar/filters above and the
              legend below stay fixed regardless of row count. */}
          {/* items-start (not the flex default of stretch) matters here:
              stretch would force both columns to the *visible* box height
              rather than their actual content height, and the timeline
              column's overflow-y-hidden (below) would then clip everything
              past that instead of just scrolling past it like the label
              column does. items-start keeps each column at its own natural
              content height, so there's nothing left for either to clip. */}
          <div className="flex min-h-0 flex-1 items-start overflow-y-auto">
            <div className="w-[310px] shrink-0 border-r border-zinc-800/60">
              {/* py-1.5 margin-equivalent breathing room before the first
                  row and after the last, matched by marginTop/marginBottom
                  on the bars container below (not padding — the bars are
                  absolutely positioned against that container's own
                  coordinate system, so margin around it is safe where
                  padding inside it would shift every bar's math). */}
              <div className="py-1.5">
                {rows.map((r) => {
                  if (r.kind === 'SUBTASK') {
                    return (
                      <div
                        key={r.id}
                        className="flex items-center gap-2 border-b border-zinc-800/50 pr-4 transition-colors"
                        style={{
                          height: ROW_H,
                          paddingLeft: 42,
                        }}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] text-zinc-300">
                            {r.subtask.title}
                          </span>
                          <span className="mt-0.5 block text-[10px] text-zinc-400">
                            {r.subtask.key} · {r.subtask.owner} ·{' '}
                            <span className="text-zinc-300">
                              {r.subtask.status}
                            </span>
                          </span>
                        </span>
                      </div>
                    );
                  }
                  const t = r.ticket;
                  const rd = readinessTone(t.readiness_status);
                  const isSel = t.deliverable_id === selectedId;
                  const isHovered = hoveredRowId === r.id;
                  return (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedId(t.deliverable_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          setSelectedId(t.deliverable_id);
                      }}
                      onMouseEnter={() => setHoveredRowId(r.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`flex w-full cursor-pointer items-center gap-2.5 border-b border-zinc-800/60 px-4 text-left transition-all duration-150 focus:outline-none focus-visible:ring-1 ${
                        isSel
                          ? 'bg-indigo-950/30'
                          : isHovered
                            ? 'bg-zinc-800/40'
                            : ''
                      }`}
                      style={{
                        height: ROW_H,
                      }}
                    >
                      {r.hasChildren ? (
                        <button
                          type="button"
                          aria-label="Toggle subtasks"
                          className="-m-1.5 shrink-0 cursor-pointer p-1.5 text-zinc-400 hover:text-zinc-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpanded((p) => ({
                              ...p,
                              [t.deliverable_id]: !p[t.deliverable_id],
                            }));
                          }}
                        >
                          <Caret open={!!expanded[t.deliverable_id]} />
                        </button>
                      ) : (
                        <span className="w-[10px] shrink-0" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-zinc-100">
                          {t.jira.title}
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-400">
                          <span className="font-medium text-zinc-400">
                            {t.external_ticket_id || 'Internal'}
                          </span>
                          <span>·</span>
                          <span className="text-zinc-300">{t.jira.owner}</span>
                          <span>·</span>
                          <span
                            style={{ color: rd.fg }}
                            className="font-medium"
                          >
                            {rd.label}
                          </span>
                        </span>
                      </span>
                      {/* Catalogue learnings diamond hidden for now,
                          alongside the legend entry and stats mentions
                          below — not ready to surface yet. */}
                    </div>
                  );
                })}
              </div>
              {rows.length === 0 && (
                <div className="px-4 py-8 text-center text-xs text-zinc-500">
                  Nothing matches these filters.
                </div>
              )}
            </div>

            <div
              ref={scrollRef}
              // overflow-y-hidden is deliberate, not redundant: per the CSS
              // overflow spec, setting only overflow-x to a non-visible
              // value forces the browser to compute the unset overflow-y
              // as auto too -- so without this, this div's own content
              // (exactly as tall as the body div wrapping it) got a second,
              // independent vertical scrollbar stacked on the body's.
              className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
              onScroll={(e) => {
                // Real scrollbar lives here; mirror its horizontal offset
                // onto the fixed header above so the date ticks keep
                // lining up with the bars as this scrolls.
                if (headerScrollRef.current)
                  headerScrollRef.current.scrollLeft =
                    e.currentTarget.scrollLeft;
              }}
            >
              <div
                style={{ width: gridW, minWidth: '100%', position: 'relative' }}
              >
                <div
                  style={{
                    position: 'relative',
                    height: rows.length * ROW_H,
                    // Matches the left column's py-1.5 (6px) exactly, so
                    // each bar stays aligned with its label row.
                    marginTop: 6,
                    marginBottom: 6,
                  }}
                >
                  {ticks.map((t, i) => (
                    <div
                      key={i}
                      className="absolute bottom-0 top-0"
                      style={{
                        left: t.x,
                        width: 1,
                        background: 'rgba(39,39,42,0.6)',
                      }}
                    />
                  ))}

                  {/* Scoped to the rows only, not the header — a version
                      that spanned the whole column ran right through the
                      date labels up there. No label on the line itself
                      either; the marker is enough on its own. */}
                  <div
                    className="pointer-events-none absolute bottom-0 top-0 z-20"
                    style={{
                      left: xOf(TODAY),
                      width: 2,
                      background: '#5652CF',
                      boxShadow: '0 0 10px rgba(86,82,207,0.7)',
                    }}
                  />

                  <svg
                    className="pointer-events-none absolute inset-0 z-0"
                    width={gridW}
                    height={rows.length * ROW_H}
                  >
                    {connectors.map((c) => {
                      const midX = Math.max(c.x1 + 8, c.x2 - 8);
                      const stroke = c.late ? T.danger : T.muted;
                      return (
                        <g key={c.key} opacity={c.late ? 0.85 : 0.45}>
                          <path
                            d={`M ${c.x1} ${c.y1} H ${midX} V ${c.y2} H ${c.x2}`}
                            fill="none"
                            stroke={stroke}
                            strokeWidth="1.4"
                            strokeDasharray={c.late ? '3 3' : undefined}
                          />
                          <path
                            d={`M ${c.x2} ${c.y2} l -4 -3 v 6 z`}
                            fill={stroke}
                          />
                        </g>
                      );
                    })}
                  </svg>

                  {rows.map((r, i) => {
                    const isSub = r.kind === 'SUBTASK';
                    const j = isSub ? r.subtask : r.ticket.jira;
                    const win = barWindow(j);
                    const state = isSub
                      ? subtaskState(r.subtask)
                      : riskState(r.ticket);
                    const tone = riskTone(state);
                    const x = xOf(win.start);
                    const w = Math.max(pxPerDay * 0.8, xOf(win.end) - x);
                    const isSel =
                      !isSub && r.ticket.deliverable_id === selectedId;
                    const isHovered = !isSub && hoveredRowId === r.id;
                    const barH = isSub ? BAR_H - 6 : BAR_H;
                    return (
                      <div
                        key={r.id}
                        onMouseEnter={() => !isSub && setHoveredRowId(r.id)}
                        onMouseLeave={() => !isSub && setHoveredRowId(null)}
                        className={`absolute left-0 right-0 transition-colors ${
                          isSel
                            ? 'bg-indigo-950/30'
                            : isHovered
                              ? 'bg-zinc-800/40'
                              : ''
                        }`}
                        style={{
                          top: i * ROW_H,
                          height: ROW_H,
                          borderBottom: '1px solid rgba(39,39,42,0.6)',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            !isSub && setSelectedId(r.ticket.deliverable_id)
                          }
                          className="absolute z-10 rounded shadow-sm transition-transform hover:scale-y-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]"
                          style={{
                            left: x,
                            top: (ROW_H - barH) / 2,
                            width: w,
                            height: barH,
                            background: win.hollow ? 'transparent' : tone.bar,
                            border: win.hollow
                              ? `1.5px dashed ${tone.bar}`
                              : 'none',
                            opacity: state === 'DONE' ? 0.65 : isSub ? 0.85 : 1,
                            cursor: isSub ? 'default' : 'pointer',
                          }}
                          aria-label={
                            isSub
                              ? `${r.subtask.title}, ${tone.label}`
                              : `${r.ticket.jira.title}, ${tone.label}, ${readinessTone(r.ticket.readiness_status).label}`
                          }
                        />
                        {!isSub && (
                          <div
                            className="absolute z-10 flex items-center gap-1.5 whitespace-nowrap"
                            style={{ left: x + w + 8, top: (ROW_H - 14) / 2 }}
                          >
                            {hasWarning(r.ticket) && (
                              <WarnIcon
                                color={
                                  ['BLOCKED', 'OVERDUE'].includes(state)
                                    ? T.danger
                                    : T.warn
                                }
                              />
                            )}
                            <span
                              className="text-[11px] font-medium"
                              style={{
                                color: readinessTone(r.ticket.readiness_status)
                                  .fg,
                              }}
                            >
                              {readinessTone(r.ticket.readiness_status).label}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-4 border-t border-zinc-800/60 bg-zinc-950/60 px-4 py-2 text-xs font-medium text-zinc-400">
            {['ON_TRACK', 'AT_RISK', 'BLOCKED', 'OVERDUE', 'DONE'].map((k) => {
              const t = riskTone(k);
              return (
                <span key={k} className="flex items-center gap-1.5">
                  <span
                    className="shadow-xs inline-block h-2 w-3.5 rounded"
                    style={{
                      background: t.bar,
                    }}
                  />
                  <span>{t.label}</span>
                </span>
              );
            })}
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-3.5 rounded border border-dashed border-sky-400" />
              <span>Not started</span>
            </span>
            {/* Catalogue learnings legend entry hidden for now, alongside
                the per-row diamond and stats mentions above — not ready to
                surface yet. */}
            {/* Explains the vertical line in the chart the same way every
                other color on it is explained — here, not as a floating
                label on the line itself, which is what overlapped the
                header's date labels before. */}
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-3.5 w-0.5 rounded-full"
                style={{
                  background: '#5652CF',
                  boxShadow: '0 0 4px rgba(86,82,207,0.7)',
                }}
              />
              <span>Today</span>
            </span>
          </div>
        </div>
      )}

      <DrawerDetail open={!!selectedId} onClose={() => setSelectedId(null)}>
        {selectedWithScope && (
          <GanttDetailPanel
            ticket={selectedWithScope}
            allTickets={tickets}
            onClose={() => setSelectedId(null)}
            onOpenTicket={setSelectedId}
          />
        )}
      </DrawerDetail>
    </div>
  );
};

export default InsightsGantt;
